import { addDays } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import {
  anchorYmdForIsoDowInClinic,
  browserTimeZone,
  clinicTimeZoneOf,
} from "@/lib/clinicTime";
import type { DoctorAvailability } from "@/types/domain";

/**
 * Convert a "Mon 18:00 – 23:00 in my browser zone" intent into one or two
 * authoritative clinic-zone rows that {@link DoctorAvailability} can persist.
 *
 * <p>The returned rows are always expressed in the clinic zone (e.g. IST),
 * which is the only zone the backend trusts. When the browser-zone window
 * crosses midnight in the clinic zone we split into two rows:
 *
 * <ul>
 *   <li>{@code Row A}: clinic day-of-start, clinic-start time, "23:59"</li>
 *   <li>{@code Row B}: clinic day-of-end, "00:00", clinic-end time</li>
 * </ul>
 *
 * The 1-minute fragment lost to the "23:59" cap is acceptable because slots
 * are 15-minute aligned — the last full slot before midnight always ends at
 * "23:45". If the browser window does <b>not</b> cross midnight in the
 * clinic zone (most common case in the same hemisphere) a single row is
 * returned.
 */
export function clinicRowsFromBrowserWindow(args: {
  /** ISO-8601 weekday picked by the doctor in their browser (1 = Mon … 7 = Sun). */
  browserDow: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** "HH:mm" in the doctor's browser zone. */
  browserStart: string;
  /** "HH:mm" in the doctor's browser zone (must be after start). */
  browserEnd: string;
  /** Optional explicit clinic zone (server-supplied wins over env fallback). */
  clinicZone?: string | null;
}): DoctorAvailability[] {
  const browserZone = browserTimeZone();
  const clinic = clinicTimeZoneOf(args.clinicZone);

  const anchorYmd = anchorYmdForBrowserDow(args.browserDow, browserZone);

  const startInstant = fromZonedTime(
    `${anchorYmd}T${args.browserStart}:00`,
    browserZone,
  );
  const endInstant = fromZonedTime(
    `${anchorYmd}T${args.browserEnd}:00`,
    browserZone,
  );
  if (endInstant.getTime() <= startInstant.getTime()) {
    return [];
  }

  const startClinicYmd = formatInTimeZone(startInstant, clinic, "yyyy-MM-dd");
  const endClinicYmd = formatInTimeZone(endInstant, clinic, "yyyy-MM-dd");
  const startClinicHHmm = formatInTimeZone(startInstant, clinic, "HH:mm");
  const endClinicHHmm = formatInTimeZone(endInstant, clinic, "HH:mm");
  const startClinicDow = Number(formatInTimeZone(startInstant, clinic, "i")) as DoctorAvailability["dayOfWeek"];
  const endClinicDow = Number(formatInTimeZone(endInstant, clinic, "i")) as DoctorAvailability["dayOfWeek"];

  if (startClinicYmd === endClinicYmd) {
    return [
      {
        dayOfWeek: startClinicDow,
        startTime: startClinicHHmm,
        endTime: endClinicHHmm,
        isActive: true,
      },
    ];
  }

  // Cross-midnight (in clinic zone) — split into two rows.
  const rows: DoctorAvailability[] = [];
  if (startClinicHHmm < "23:59") {
    rows.push({
      dayOfWeek: startClinicDow,
      startTime: startClinicHHmm,
      endTime: "23:59",
      isActive: true,
    });
  }
  if (endClinicHHmm > "00:00") {
    rows.push({
      dayOfWeek: endClinicDow,
      startTime: "00:00",
      endTime: endClinicHHmm,
      isActive: true,
    });
  }
  return rows;
}

function anchorYmdForBrowserDow(
  browserDow: number,
  browserZone: string,
  now = new Date(),
): string {
  // Find a date within the next 21 days that lands on `browserDow` in the
  // browser zone — we'll use that date to anchor the wall-clock conversion.
  const todayBrowserYmd = formatInTimeZone(now, browserZone, "yyyy-MM-dd");
  const todayDow = Number(formatInTimeZone(now, browserZone, "i"));
  const offset = (browserDow - todayDow + 7) % 7;
  const anchor = fromZonedTime(`${todayBrowserYmd}T12:00:00`, browserZone);
  return formatInTimeZone(addDays(anchor, offset), browserZone, "yyyy-MM-dd");
}

/**
 * Lightweight description of how the doctor's row will be presented in the
 * browser zone (for read-only hint rendering on each row of the schedule).
 */
export interface BrowserViewOfClinicRow {
  /** "HH:mm" in the browser zone matching the clinic start instant. */
  browserStart: string;
  /** "HH:mm" in the browser zone matching the clinic end instant. */
  browserEnd: string;
  /** ISO weekday in the browser zone for clinic start (1 = Mon …). */
  browserStartDow: number;
  /** ISO weekday in the browser zone for clinic end (1 = Mon …). */
  browserEndDow: number;
  /** True when the rendered window crosses midnight in the browser zone. */
  daySpans: boolean;
}

/**
 * Renders a single clinic-zone weekly row as it would appear in the
 * doctor's browser zone — used for the "= 13:30 BST · Mon" hint.
 */
export function browserViewOfClinicRow(
  dow: number,
  clinicStartHHmm: string,
  clinicEndHHmm: string,
  clinicZone?: string | null,
): BrowserViewOfClinicRow | null {
  if (
    !/^\d{2}:\d{2}$/.test(clinicStartHHmm) ||
    !/^\d{2}:\d{2}$/.test(clinicEndHHmm)
  ) {
    return null;
  }
  const ymd = anchorYmdForIsoDowInClinic(dow, new Date(), clinicZone);
  const clinic = clinicTimeZoneOf(clinicZone);
  const browser = browserTimeZone();
  const startInstant = fromZonedTime(`${ymd}T${clinicStartHHmm}:00`, clinic);
  const endInstant = fromZonedTime(`${ymd}T${clinicEndHHmm}:00`, clinic);
  return {
    browserStart: formatInTimeZone(startInstant, browser, "HH:mm"),
    browserEnd: formatInTimeZone(endInstant, browser, "HH:mm"),
    browserStartDow: Number(formatInTimeZone(startInstant, browser, "i")),
    browserEndDow: Number(formatInTimeZone(endInstant, browser, "i")),
    daySpans:
      Number(formatInTimeZone(startInstant, browser, "i")) !==
      Number(formatInTimeZone(endInstant, browser, "i")),
  };
}
